import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf } from "@phosphor-icons/react";

export default function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/app");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div
        className="hidden md:block relative overflow-hidden"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/137618/pexels-photo-137618.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-primary/70 mix-blend-multiply" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-primary-foreground">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-md bg-primary-foreground/20 border border-primary-foreground/30 flex items-center justify-center backdrop-blur-md">
              <Leaf size={22} weight="fill" />
            </div>
            <div className="font-display text-lg font-semibold">Enasuspro® Carbon Ledger</div>
            <div className="text-[10px] uppercase tracking-widest opacity-70 ml-auto">Sustainability Coaching</div>
          </div>
          <div>
            <h2 className="font-display text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
              {t("heroHeadline")}
            </h2>
            <p className="mt-4 text-base opacity-90 max-w-md">{t("heroSub")}</p>
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              {["Scope 1", "Scope 2", "Scope 3"].map((s) => (
                <div key={s} className="border border-primary-foreground/30 rounded-md px-3 py-2 text-center backdrop-blur-sm">
                  <div className="font-mono-data text-xs opacity-70">GHG</div>
                  <div className="font-display font-medium">{s}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-10">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="login-form">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data mb-2">ISO 14064 · GHG</div>
            <h1 className="font-display text-3xl font-semibold">{t("welcomeBack")}</h1>
            <p className="text-muted-foreground text-sm mt-1">{t("tagline")}</p>
          </div>
          {err && (
            <div className="text-sm rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2" data-testid="login-error">
              {err}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">{t("email")}</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="login-email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("password")}</Label>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="login-password"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
            {loading ? "…" : t("signIn")}
          </Button>
          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-widest font-mono-data">
              <span className="bg-background px-2 text-muted-foreground">{t("or")}</span>
            </div>
          </div>
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            type="button"
            onClick={() => {
              const redirectUrl = window.location.origin + "/auth-callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full h-10 rounded-md border border-border bg-background hover:bg-secondary transition-colors flex items-center justify-center gap-2 text-sm font-medium"
            data-testid="login-google"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {t("signInWithGoogle")}
          </button>
          <div className="text-sm text-muted-foreground">
            {t("dontHaveAccount")}{" "}
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="go-register">
              {t("signUp")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
